import { ParsedQuery } from "query-string";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { environment } from "environments/environment";
import { Alert } from "utils/services";

/** Current auth token, updated via {@link setSession} or a parent token refresh. */
let authToken = "";

/** How long to wait for the parent app to answer a token-refresh request. */
const REFRESH_TIMEOUT_MS = 8000;

/**
 * Store the auth token from the query params for use on all subsequent HTTP calls.
 * Safe to call repeatedly (e.g. on every render) — it only updates the token.
 * @param {ParsedQuery} queryParams Query Params object parsed via query-string npm
 */
const setSession = (queryParams: ParsedQuery) => {
  authToken = `${queryParams.token ?? ""}`;
};

// Register the request interceptor exactly once at module load. Reads the latest
// token via closure so repeated setSession calls don't stack up interceptors.
axios.interceptors.request.use(
  (request: InternalAxiosRequestConfig<any>) => {
    // Don't force JSON onto FormData bodies (e.g. photo uploads) — the browser
    // needs to set its own Content-Type with the multipart boundary.
    if (!request.headers["Content-Type"] && !(request.data instanceof FormData)) {
      request.headers["Content-Type"] = "application/json";
    }
    request.headers["Ocp-Apim-Subscription-Key"] = request.url?.includes(environment.reportServerUrl) ? environment.reportSubscriptionKey : environment.apiSubscriptionKey;
    request.headers["Ocp-Apim-Trace"] = "false";
    request.headers["Authorization"] = `Bearer ${authToken}`;
    return request;
  },
  (err) => Promise.reject(err)
);

/**
 * Single in-flight token refresh. The session token is minted by the parent app
 * and handed to us via the `?token=` query param at load — it can expire mid-session,
 * which surfaces as 401s on save. Rather than fail (and lose the user's work) we ask
 * the parent for a fresh token over postMessage and retry the failed request.
 *
 * Multiple concurrent 401s share the same promise so we only prompt the parent once.
 */
let refreshInFlight: Promise<string> | null = null;

// Guard so concurrent failed requests don't stack up duplicate "session expired"
// dialogs. Reset once a refresh succeeds, so a later expiry can notify again.
let sessionExpiredNotified = false;

const requestTokenFromParent = (): Promise<string> => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = new Promise<string>((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      refreshInFlight = null;
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("Token refresh timed out — parent app did not respond."));
    }, REFRESH_TIMEOUT_MS);

    const onMessage = (event: MessageEvent) => {
      // SECURITY: before GA, validate event.origin against a configured parent
      // origin. For now we require the message to come from our embedder.
      if (event.source !== window.parent && event.source !== window.top) return;

      let payload: { action?: string; token?: unknown };
      try {
        payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }

      if (payload?.action !== "token" || typeof payload.token !== "string" || !payload.token) return;

      authToken = payload.token;
      sessionExpiredNotified = false;
      cleanup();
      resolve(payload.token);
    };

    window.addEventListener("message", onMessage);
    // Ask the embedding app for a fresh token. Mirrors the existing
    // window.top.postMessage({ action: "close" }) convention.
    window.top?.postMessage(JSON.stringify({ action: "refresh-token" }), "*");
  });

  return refreshInFlight;
};

// On 401, refresh the token via the parent and retry the original request once.
// If the parent can't (or won't) refresh, the 401 propagates to the caller, which
// is responsible for surfacing it and protecting unsaved work.
axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      try {
        const token = await requestTokenFromParent();
        original.headers.set("Authorization", `Bearer ${token}`);
        return axios(original);
      } catch {
        // Refresh failed: the parent couldn't mint a fresh token (e.g. its
        // Firebase refresh token is revoked/expired). The parent won't boot the
        // user until they next route or reload, so until then they'd just see a
        // silent failure. Tell them what happened and what to do. Tag the error
        // so the per-call handlers don't clobber this with a generic alert.
        if (!sessionExpiredNotified) {
          sessionExpiredNotified = true;
          Alert.simpleAlert({
            header: "Session expired",
            message:
              "Your sign-in session has expired, so your latest changes could not be saved.\n\nPlease refresh this page to sign in again. Any unsaved edits will need to be re-applied.",
          });
        }
        (error as AxiosError & { sessionExpired?: boolean }).sessionExpired = true;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * The freshest auth token the editor holds — seeded from the URL and updated by
 * parent token refreshes. Use this (not the stale `?token=` query param) when
 * handing a token to a new context, e.g. the preview popup, so it doesn't open
 * with an already-expired token it can't refresh on its own.
 */
export const getAuthToken = () => authToken;

export default setSession;
